import { memo } from 'react';
import { AlertCircle, Check, CheckCheck, Clock, Eye } from 'lucide-react';
import { Tooltip, cn } from '@cyphex/ui';
import type { DeliveryState } from '../../types';

interface MessageMetaProps {
  timestamp: string;
  edited: boolean;
  delivery: DeliveryState | null;
  className?: string;
}

const DELIVERY_META: Record<DeliveryState, { icon: typeof Check; label: string; tone: string }> = {
  queued: { icon: Clock, label: 'Waiting to send', tone: 'text-muted-foreground' },
  sending: { icon: Clock, label: 'Sending', tone: 'text-muted-foreground' },
  sent: { icon: Check, label: 'Sent', tone: 'text-muted-foreground' },
  delivered: { icon: CheckCheck, label: 'Delivered', tone: 'text-muted-foreground' },
  /* Muted, like every other delivery state. In brand purple this was the
     loudest thing on the screen — a receipt is a reassurance you glance at, not
     a thing that should out-shout the message it belongs to. The ICON already
     distinguishes read from sent; the colour was doing no extra work. */
  read: { icon: Eye, label: 'Read', tone: 'text-muted-foreground' },
  failed: { icon: AlertCircle, label: 'Not sent', tone: 'text-destructive' },
};

/**
 * The delivery glyph on its own.
 *
 * It needs to exist apart from the meta line because the tick belongs on the
 * LAST message you sent, and a run of messages has exactly one header — on its
 * FIRST. Rendering the tick only inside that header meant a grouped message
 * could never show one: send "ok" then "?" and the receipt stayed pinned to
 * whatever you said before them, which reads as "not updating".
 */
export function DeliveryTick({
  delivery,
  className,
}: {
  delivery: DeliveryState | null;
  className?: string;
}) {
  const meta = delivery ? DELIVERY_META[delivery] : null;
  if (meta === null) return null;
  const Icon = meta.icon;
  return (
    <Tooltip content={meta.label}>
      <span className={cn('inline-flex', meta.tone, className)}>
        <Icon size={14} strokeWidth={2.5} aria-label={meta.label} />
      </span>
    </Tooltip>
  );
}

/** Timestamp + edited marker + delivery tick. */
export const MessageMeta = memo(function MessageMeta({
  timestamp,
  edited,
  delivery,
  className,
}: MessageMetaProps) {
  return (
    <span className={cn('inline-flex items-center gap-1 text-sm text-muted-foreground', className)}>
      <time>{timestamp}</time>
      {edited ? <span aria-label="Edited">· edited</span> : null}
      <DeliveryTick delivery={delivery} />
    </span>
  );
});
